import { FolderUp, type LucideIcon } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import {
	FileUploader,
	type UploadMode,
} from "src/components/Uploader/FileUploader";

interface UploadedFileData {
	file: Blob;
	name: string;
}

interface UploadModalProps {
	onClose: () => void;
	onConfirm: (
		file: UploadedFileData,
		extra?: Record<string, unknown>,
	) => void | Promise<void>;
	mode?: UploadMode;
	allowedFormats?: string;
	accept?: string;
	title?: string;
	Icon?: LucideIcon;
	confirmDisabled?: boolean;
	children?: (file: UploadedFileData | null) => React.ReactNode;
}

export const UploadModal = ({
	onClose,
	onConfirm,
	mode = "upload",
	allowedFormats,
	accept,
	title,
	Icon = FolderUp,
	confirmDisabled = false,
	children,
}: UploadModalProps) => {
	const { t } = useTranslation();
	const [file, setFile] = useState<UploadedFileData | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	const handleConfirm = async () => {
		if (!file) return;
		setIsSaving(true);
		try {
			await onConfirm(file);
			onClose();
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal onClose={onClose} maxWidth={720}>
			<ModalHeader
				Icon={Icon}
				title={title || t("uploader.importFiles", "Importuj pliki")}
				hasCloseButton
			/>
			<div className="flex flex-col w-100 mt-24">
				<FileUploader
					mode={mode}
					allowedFormats={allowedFormats}
					accept={accept}
					onFileChange={setFile}
					file={file}
				/>
				{children?.(file)}
			</div>
			<ModalFooter className="justify-end gap-12 mt-24 pt-16 border-top-1">
				<TextPrimaryButton onClick={onClose}>
					{t("commands.cancel", "Anuluj")}
				</TextPrimaryButton>
				<PrimaryButton
					onClick={handleConfirm}
					disabled={!file || confirmDisabled}
					isLoading={isSaving}
				>
					{t("commands.save", "Zapisz")}
				</PrimaryButton>
			</ModalFooter>
		</Modal>
	);
};
